import { Controller } from "@hotwired/stimulus"

const DESKTOP_MQ = "(min-width: 768px)"
const MEGA_TRANSITION_MS = 200
const OVER_HERO_CLASS = "site-nav--over-hero"
const OVERLAY_SCROLL_LIMIT_PX = 30

export default class extends Controller {
  static values = { overlay: Boolean }

  static targets = [
    "scrim",
    "mega",
    "panel",
    "trigger",
    "drawer",
    "accountDrawer",
    "hamburger",
    "hamburgerIcon",
    "hamburgerCloseIcon",
    "nestedBranch",
    "nestedTrigger",
    "nestedPanel"
  ]

  connect() {
    this.closeDesktopTimer = null
    this.megaCloseTimer = null
    this.accountCloseTimer = null
    this.beforeCacheHandler = this.closeAll.bind(this)
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler)
    this.escapeHandler = this.onEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)
    if (this.overlayValue) this.syncOverlayClass()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    document.removeEventListener("keydown", this.escapeHandler)
    this.clearCloseDesktopTimer()
    this.clearMegaCloseTimer()
    this.clearAccountCloseTimer()
    this.unlockPageScroll()
  }

  syncOverlayClass() {
    if (!this.overlayValue) return

    this.element.classList.toggle(OVER_HERO_CLASS, window.scrollY <= OVERLAY_SCROLL_LIMIT_PX)
  }

  onEscape(event) {
    if (event.key !== "Escape") return
    this.closeAll()
  }

  isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches
  }

  openDesktopPanel(event) {
    if (!this.isDesktop()) return

    this.clearCloseDesktopTimer()
    const key = event.params.panel
    if (!key) return

    this.closeMobile()
    this.showScrim()
    this.setActiveTriggers(key)

    const panel = this.panelTargets.find((el) => el.dataset.siteNavPanel === key)
    if (!panel) return

    if (key === "account") {
      this.openAccountPanel()
      this.closeMega()
      return
    }

    this.clearMegaCloseTimer()
    this.closeAccountPanel()
    this.panelTargets.forEach((el) => {
      if (el.dataset.siteNavPanel === "account") return
      el.hidden = el.dataset.siteNavPanel !== key
    })
    panel.hidden = false
    this.openMega()
  }

  scheduleCloseDesktop() {
    if (!this.isDesktop()) return

    this.clearCloseDesktopTimer()
    this.closeDesktopTimer = window.setTimeout(() => this.closeDesktop(), 120)
  }

  cancelCloseDesktop() {
    this.clearCloseDesktopTimer()
  }

  closeNavOnClick(event) {
    if (!event.target.closest("a[href], button[type='submit']")) return

    this.closeAll()
  }

  clearCloseDesktopTimer() {
    if (this.closeDesktopTimer) {
      window.clearTimeout(this.closeDesktopTimer)
      this.closeDesktopTimer = null
    }
  }

  clearMegaCloseTimer() {
    if (this.megaCloseTimer) {
      window.clearTimeout(this.megaCloseTimer)
      this.megaCloseTimer = null
    }
  }

  clearAccountCloseTimer() {
    if (this.accountCloseTimer) {
      window.clearTimeout(this.accountCloseTimer)
      this.accountCloseTimer = null
    }
  }

  accountPanel() {
    return this.panelTargets.find((el) => el.dataset.siteNavPanel === "account")
  }

  openAccountPanel() {
    const panel = this.accountPanel()
    if (!panel) return

    this.clearAccountCloseTimer()
    panel.hidden = false
    panel.offsetHeight
    panel.classList.add("site-nav-account-panel--open")
    panel.setAttribute("aria-hidden", "false")
  }

  closeAccountPanel() {
    const panel = this.accountPanel()
    if (!panel) return

    panel.classList.remove("site-nav-account-panel--open")
    panel.setAttribute("aria-hidden", "true")
    this.clearAccountCloseTimer()
    this.accountCloseTimer = window.setTimeout(() => {
      panel.hidden = true
      this.accountCloseTimer = null
    }, MEGA_TRANSITION_MS)
  }

  openMega() {
    if (!this.hasMegaTarget) return

    this.clearMegaCloseTimer()
    this.megaTarget.classList.add("site-nav-mega--open")
    this.megaTarget.setAttribute("aria-hidden", "false")
  }

  closeMega() {
    if (!this.hasMegaTarget) return

    this.megaTarget.classList.remove("site-nav-mega--open")
    this.megaTarget.setAttribute("aria-hidden", "true")
    this.clearMegaCloseTimer()
    this.megaCloseTimer = window.setTimeout(() => {
      this.hideMegaPanels()
      this.megaCloseTimer = null
    }, MEGA_TRANSITION_MS)
  }

  closeDesktop() {
    this.clearCloseDesktopTimer()
    this.clearActiveTriggers()
    this.closeAccountPanel()
    this.closeMega()
    if (!this.mobileOpen()) this.hideScrim()
  }

  hideMegaPanels() {
    this.panelTargets.forEach((panel) => {
      if (panel.dataset.siteNavPanel === "account") return
      panel.hidden = true
    })
  }

  hideAllDesktopPanels() {
    this.panelTargets.forEach((panel) => {
      panel.classList.remove("site-nav-account-panel--open")
      panel.hidden = true
      if (panel.dataset.siteNavPanel === "account") {
        panel.setAttribute("aria-hidden", "true")
      }
    })
  }

  setActiveTriggers(key) {
    this.triggerTargets.forEach((trigger) => {
      const active = trigger.dataset.siteNavPanelParam === key
      trigger.classList.toggle("site-nav-l1--open", active)
      trigger.classList.toggle("site-nav-account-trigger--open", active)
      if (trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", active ? "true" : "false")
      }
    })
  }

  clearActiveTriggers() {
    this.triggerTargets.forEach((trigger) => {
      trigger.classList.remove("site-nav-l1--open", "site-nav-account-trigger--open")
      if (trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", "false")
      }
    })
  }

  toggleMobile() {
    if (this.navDrawerOpen()) {
      this.closeMobile()
    } else {
      this.closeAccountDrawer()
      this.openNavDrawer()
    }
  }

  openNavDrawer() {
    if (!this.hasDrawerTarget) return

    this.closeDesktop()
    this.drawerTarget.hidden = false
    this.showScrim()
    this.lockPageScroll()
    this.setHamburgerOpen(true)
  }

  closeMobile() {
    this.closeNavDrawer()
    this.closeAccountDrawer()
    this.collapseAllNested()
    this.unlockPageScroll()
    this.setHamburgerOpen(false)
    if (this.desktopClosed()) this.hideScrim()
  }

  closeNavDrawer() {
    if (this.hasDrawerTarget) this.drawerTarget.hidden = true
  }

  closeAccountDrawer() {
    if (this.hasAccountDrawerTarget) this.accountDrawerTarget.hidden = true
  }

  openAccountMobile(event) {
    if (this.isDesktop()) return

    event.preventDefault()
    this.closeDesktop()
    this.closeNavDrawer()
    this.setHamburgerOpen(false)

    if (!this.hasAccountDrawerTarget) return

    this.accountDrawerTarget.hidden = false
    this.showScrim()
    this.lockPageScroll()
  }

  lockPageScroll() {
    if (document.documentElement.classList.contains("site-nav-drawer-open")) return

    this.lockedScrollY = window.scrollY
    document.documentElement.classList.add("site-nav-drawer-open")
    document.body.classList.add("site-nav-drawer-open")
    // scrollY is measured at lock time; a class cannot encode that pixel offset.
    document.body.style.top = `-${this.lockedScrollY}px`
  }

  unlockPageScroll() {
    if (!document.documentElement.classList.contains("site-nav-drawer-open")) return

    document.documentElement.classList.remove("site-nav-drawer-open")
    document.body.classList.remove("site-nav-drawer-open")
    document.body.style.top = ""
    window.scrollTo(0, this.lockedScrollY || 0)
    this.lockedScrollY = 0
  }

  openNavFromAccount(event) {
    if (event) event.preventDefault()
    if (this.isDesktop()) return

    this.closeAccountDrawer()
    this.openNavDrawer()
  }

  toggleNested(event) {
    const trigger = event.currentTarget
    const branch = this.nestedBranchTargets.find((el) => el.contains(trigger))
    if (!branch) return

    const panel = this.nestedPanelTargets.find((el) => branch.contains(el))
    if (!panel) return

    const opening = !branch.classList.contains("site-nav-drawer-branch--open")
    branch.classList.toggle("site-nav-drawer-branch--open", opening)
    trigger.setAttribute("aria-expanded", opening ? "true" : "false")
    panel.setAttribute("aria-hidden", opening ? "false" : "true")
  }

  collapseAllNested() {
    this.nestedBranchTargets.forEach((branch) => {
      branch.classList.remove("site-nav-drawer-branch--open")
      const panel = this.nestedPanelTargets.find((el) => branch.contains(el))
      if (panel) panel.setAttribute("aria-hidden", "true")
      const trigger = this.nestedTriggerTargets.find((el) => branch.contains(el))
      if (trigger) trigger.setAttribute("aria-expanded", "false")
    })
  }

  mobileOpen() {
    return this.navDrawerOpen() || this.accountDrawerOpen()
  }

  navDrawerOpen() {
    return this.hasDrawerTarget && !this.drawerTarget.hidden
  }

  accountDrawerOpen() {
    return this.hasAccountDrawerTarget && !this.accountDrawerTarget.hidden
  }

  desktopClosed() {
    return this.panelTargets.every((panel) => panel.hidden)
  }

  setHamburgerOpen(open) {
    if (this.hasHamburgerTarget) {
      this.hamburgerTarget.setAttribute("aria-expanded", open ? "true" : "false")
      this.hamburgerTarget.setAttribute("aria-label", open ? "Close menu" : "Menu")
    }
    if (this.hasHamburgerIconTarget) {
      this.hamburgerIconTarget.classList.toggle("hidden", open)
    }
    if (this.hasHamburgerCloseIconTarget) {
      this.hamburgerCloseIconTarget.classList.toggle("hidden", !open)
    }
  }

  showScrim() {
    if (this.hasScrimTarget) this.scrimTarget.hidden = false
  }

  hideScrim() {
    if (this.hasScrimTarget) this.scrimTarget.hidden = true
  }

  closeAll() {
    this.clearCloseDesktopTimer()
    this.clearMegaCloseTimer()
    this.clearAccountCloseTimer()
    this.clearActiveTriggers()
    this.hideAllDesktopPanels()
    if (this.hasMegaTarget) {
      this.megaTarget.classList.remove("site-nav-mega--open")
      this.megaTarget.setAttribute("aria-hidden", "true")
    }
    this.closeMobile()
    this.hideScrim()
  }
}
